import React, { useEffect, useState } from 'react';
import { fetchClockSupportSubmissions } from '../../services/emails';

const APP_STORE_URL = 'https://apps.apple.com/app/id0000000000';
const SUPPORT_EMAIL = 'aevdir@163.com';

const COPY = {
  en: {
    eyebrow: 'iOS 26 · AlarmKit',
    title: 'Clock — Wake Up Duck',
    tagline: 'System-level alarms that actually wake you up.',
    lead:
      'Clock helps you wake up on time with alarms that lean on iOS 26+ AlarmKit for system-level alerting. Smart workday & holiday rules, configurable snooze, and a curated set of bundled tones.',
    primaryCta: 'View on App Store',
    secondaryCta: 'Contact support',
    badgeAvailable: 'Available on iPhone',
    badgeRequires: 'Requires iOS 26.0 or later',
    featuresTitle: 'Why Wake Up Duck',
    features: [
      {
        title: 'System-level alerting',
        body:
          'Built on AlarmKit so iOS surfaces the alarm at the right moment — even in Silent and Focus modes (subject to system policy).',
      },
      {
        title: 'Flexible schedules',
        body:
          'Once, daily, weekly, or workday-only. Pair workday with China or US federal holiday calendars to skip days off automatically.',
      },
      {
        title: 'Holiday calendars built in',
        body:
          'Browse upcoming China / US holidays in-app. Weekends and statutory holidays are clearly differentiated.',
      },
      {
        title: 'Configurable snooze',
        body: 'Pick the snooze interval that fits your morning, with predictable repeats.',
      },
      {
        title: 'Bundled tones',
        body: 'Several gentle, hand-tuned alarm sounds out of the box — no extra downloads.',
      },
      {
        title: 'Privacy-first',
        body:
          'No tracking SDKs. Your alarms and schedules stay on-device; see the privacy policy for details.',
      },
    ],
    detailsTitle: 'Built for iOS 26+',
    detailsBody:
      'Wake Up Duck targets iPhone running iOS 26 and above. AlarmKit must be granted permission so the system can present alarms reliably. A small subset of advanced scheduling features (workday alarms, holiday-aware weekly rules) is available via an in-app subscription; non-premium settings always remain editable.',
    faqTitle: 'FAQ',
    faqs: [
      {
        q: 'Why do I need iOS 26?',
        a:
          'Wake Up Duck schedules alarms via AlarmKit, which is only available on iOS 26 and above. Older versions cannot present system-level alerts in the same way.',
      },
      {
        q: 'Does the alarm work in Silent / Focus mode?',
        a:
          'Yes — AlarmKit lets the system present an alarm interface even when the device is in Silent or Focus modes. Exact behaviour depends on iOS policies.',
      },
      {
        q: 'How does the holiday-skip work?',
        a:
          'Pick a workday schedule and bind it to the China or US federal holiday calendar. The app will skip statutory holidays automatically and you can preview the calendar in-app.',
      },
      {
        q: 'How do I cancel the subscription?',
        a:
          'Manage or cancel any time from Settings → Apple ID → Subscriptions on your iPhone. Existing settings are preserved if the subscription lapses.',
      },
    ],
    feedbackTitle: 'User feedback',
    feedbackLead: 'Recent messages from Wake Up Duck users (via support form).',
    feedbackEmpty: 'No feedback yet. Be the first to reach out via support.',
    feedbackError: 'Could not load feedback. Please try again later.',
    appNameLabel: 'Wake Up Duck',
    bundleLabel: 'Bundle ID',
    bundleId: 'com.aevdir.clock',
    minOsLabel: 'Minimum iOS',
    minOsValue: 'iOS 26.0',
    contactPrompt: 'Questions or feedback? Email',
  },
  zh: {
    eyebrow: 'iOS 26 · AlarmKit',
    title: '醒醒鸭 — 系统级闹钟',
    tagline: '能真正叫醒你的闹钟。',
    lead:
      '醒醒鸭基于 iOS 26+ AlarmKit，由系统在合适场景呈现闹钟界面。支持工作日与中/美节假日规则、可配置的稍后提醒、以及多首精心挑选的内置铃声。',
    primaryCta: '前往 App Store',
    secondaryCta: '联系支持',
    badgeAvailable: '支持 iPhone',
    badgeRequires: '需要 iOS 26.0 或更高',
    featuresTitle: '为什么选择醒醒鸭',
    features: [
      {
        title: '系统级响铃',
        body: '基于 AlarmKit，由系统在合适场景呈现闹钟界面，在静音、专注模式下也能尽力到达（具体表现以系统为准）。',
      },
      {
        title: '灵活的重复方式',
        body: '不重复、每天、每周自定义，以及「工作日」模式；可绑定中国或美国联邦假日数据，自动跳过法定假日。',
      },
      {
        title: '内置节假日日历',
        body: '在应用内即可浏览中/美节假日，周末与法定假日以不同方式区分展示。',
      },
      {
        title: '可配置的稍后提醒',
        body: '自定义 Snooze 间隔，按你的节奏稳定再提醒。',
      },
      {
        title: '内置精选铃声',
        body: '多首温和、手工调音的闹铃可直接使用，无需额外下载。',
      },
      {
        title: '尊重你的隐私',
        body: '没有第三方追踪 SDK，闹钟与日程保留在设备本地，详情见隐私政策。',
      },
    ],
    detailsTitle: '专为 iOS 26+ 打造',
    detailsBody:
      '醒醒鸭面向运行 iOS 26 及以上的 iPhone。请允许 AlarmKit 权限，以便系统稳定呈现闹钟。部分进阶能力（工作日闹钟、按中/美节假日规则的每周模式）以应用内订阅形式提供；未订阅时设置仍会保留。',
    faqTitle: '常见问题',
    faqs: [
      {
        q: '为什么需要 iOS 26？',
        a: '醒醒鸭使用 AlarmKit 调度闹钟，该框架仅在 iOS 26 及以上提供。旧版系统无法以同样方式呈现系统级闹钟界面。',
      },
      {
        q: '在静音 / 专注模式下能响吗？',
        a: '可以。AlarmKit 允许系统在静音或专注模式下仍呈现闹钟界面，具体行为以 iOS 系统策略为准。',
      },
      {
        q: '节假日跳过是怎么工作的？',
        a: '选择「工作日」时间表，并绑定中国或美国联邦假日日历，应用会自动跳过法定假日。你可以在应用内预览该日历。',
      },
      {
        q: '如何取消订阅？',
        a: '可在 iPhone 的「设置 → Apple ID → 订阅」中随时管理或取消。订阅到期后已有设置仍会保留。',
      },
    ],
    feedbackTitle: '用户反馈',
    feedbackLead: '来自醒醒鸭用户的近期留言（通过支持渠道提交）。',
    feedbackEmpty: '暂无反馈，欢迎通过支持页面率先留言。',
    feedbackError: '反馈加载失败，请稍后再试。',
    appNameLabel: '醒醒鸭',
    bundleLabel: 'Bundle ID',
    bundleId: 'com.aevdir.clock',
    minOsLabel: '最低系统',
    minOsValue: 'iOS 26.0',
    contactPrompt: '有问题或建议？请邮件联系',
  },
};

function formatFeedbackTime(isoString, lang) {
  if (!isoString) return '';
  try {
    const date = new Date(isoString);
    const locale = lang === 'zh' ? 'zh-CN' : 'en-US';
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  } catch {
    return isoString;
  }
}

function ClockPage({ lang, onNavigateSupport }) {
  const t = COPY[lang === 'zh' ? 'zh' : 'en'];
  const [feedback, setFeedback] = useState([]);
  const [feedbackStatus, setFeedbackStatus] = useState('loading');

  useEffect(() => {
    let cancelled = false;
    setFeedbackStatus('loading');
    fetchClockSupportSubmissions(50)
      .then((rows) => {
        if (cancelled) return;
        setFeedback(Array.isArray(rows) ? rows : []);
        setFeedbackStatus('ready');
      })
      .catch(() => {
        if (cancelled) return;
        setFeedback([]);
        setFeedbackStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSupportClick = (event) => {
    if (typeof onNavigateSupport === 'function') {
      event.preventDefault();
      onNavigateSupport();
    }
  };

  return (
    <section className="clock-page">
      <header className="clock-hero">
        <div className="clock-hero-text">
          <span className="clock-eyebrow">{t.eyebrow}</span>
          <h1 className="clock-title">{t.title}</h1>
          <p className="clock-tagline">{t.tagline}</p>
          <p className="clock-lead">{t.lead}</p>
          <div className="clock-cta-row">
            <a
              className="clock-cta-primary"
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t.primaryCta}
            </a>
            <a
              className="clock-cta-secondary"
              href="/page/support"
              onClick={handleSupportClick}
            >
              {t.secondaryCta}
            </a>
          </div>
          <div className="clock-badges">
            <span className="clock-badge">{t.badgeAvailable}</span>
            <span className="clock-badge clock-badge-muted">{t.badgeRequires}</span>
          </div>
        </div>
        <div className="clock-hero-art" aria-hidden="true">
          <div className="clock-hero-glow" />
          <img
            src="/clock-icon.png"
            alt=""
            className="clock-hero-icon"
            width="220"
            height="220"
          />
        </div>
      </header>

      <div className="clock-section">
        <h2 className="clock-section-title">{t.featuresTitle}</h2>
        <div className="clock-feature-grid">
          {t.features.map((feature) => (
            <div className="clock-feature-card" key={feature.title}>
              <h3 className="clock-feature-title">{feature.title}</h3>
              <p className="clock-feature-body">{feature.body}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="clock-section clock-details">
        <div className="clock-details-text">
          <h2 className="clock-section-title">{t.detailsTitle}</h2>
          <p className="clock-details-body">{t.detailsBody}</p>
        </div>
        <dl className="clock-spec-list">
          <div className="clock-spec-row">
            <dt>{t.appNameLabel}</dt>
            <dd>Wake Up Duck</dd>
          </div>
          <div className="clock-spec-row">
            <dt>{t.bundleLabel}</dt>
            <dd>{t.bundleId}</dd>
          </div>
          <div className="clock-spec-row">
            <dt>{t.minOsLabel}</dt>
            <dd>{t.minOsValue}</dd>
          </div>
        </dl>
      </div>

      <div className="clock-section">
        <h2 className="clock-section-title">{t.faqTitle}</h2>
        <div className="clock-faq-list">
          {t.faqs.map((faq) => (
            <details className="clock-faq-item" key={faq.q}>
              <summary>{faq.q}</summary>
              <p>{faq.a}</p>
            </details>
          ))}
        </div>
      </div>

      <div className="clock-section clock-feedback-section">
        <h2 className="clock-section-title">{t.feedbackTitle}</h2>
        <p className="clock-feedback-lead">{t.feedbackLead}</p>
        {feedbackStatus === 'loading' && (
          <p className="clock-feedback-status" aria-live="polite">
            …
          </p>
        )}
        {feedbackStatus === 'error' && (
          <p className="clock-feedback-status clock-feedback-status-error">{t.feedbackError}</p>
        )}
        {feedbackStatus === 'ready' && feedback.length === 0 && (
          <p className="clock-feedback-status">{t.feedbackEmpty}</p>
        )}
        {feedbackStatus === 'ready' && feedback.length > 0 && (
          <ul className="clock-feedback-list">
            {feedback.map((item) => (
              <li className="clock-feedback-item" key={item.uuid}>
                <div className="clock-feedback-meta">
                  <time dateTime={item.createTime}>
                    {formatFeedbackTime(item.createTime, lang)}
                  </time>
                </div>
                {item.subject ? (
                  <h3 className="clock-feedback-subject">{item.subject}</h3>
                ) : null}
                {item.message ? (
                  <p className="clock-feedback-message">{item.message}</p>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>

      <p className="clock-contact">
        {t.contactPrompt}{' '}
        <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
      </p>
    </section>
  );
}

export default ClockPage;